import { cn } from '@/lib/cn';

/**
 * The three states every list/table screen needs — loading, empty, error —
 * pulled into one file because they are always used as a trio (see
 * QueueView's `if (isLoading) ... if (isError) ... if (!data.length) ...`
 * ladder) and are trivial enough that three separate files would be pure
 * overhead. Reproduces index.css's centered-grey-text convention for these
 * states rather than inventing a new one.
 */

export function LoadingState({ label = 'Loading…', className }: { label?: string; className?: string }) {
  return (
    <div role="status" aria-busy="true" className={cn('rounded-xl border border-border bg-card p-10 text-center text-ink-muted', className)}>
      {label}
    </div>
  );
}

export function EmptyState({
  label,
  hint,
  action,
  className,
}: {
  label: string;
  hint?: string;
  /** Backlog #20's "action-bearing empty state" — the assessment's Dashboard finding was
   *  "state without action"; this is the same fix at the primitive level. Optional: most
   *  empty states in this app sit beside a header button that already does the same job
   *  (a duplicate CTA inside the card would just be noise there) — reach for this only
   *  where the empty state genuinely is the only surface offering the fix. */
  action?: { label: string; onClick: () => void };
  className?: string;
}) {
  return (
    <div className={cn('rounded-xl border border-border bg-card p-10 text-center', className)}>
      <div className="text-ink-muted">{label}</div>
      {hint && <div className="mt-1 text-xs text-ink-muted/80">{hint}</div>}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="mt-3 rounded-full bg-accent px-3.5 py-1.5 text-[12px] font-semibold text-teal hover:bg-accent/80"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

/**
 * `message` should be an ApiError's `.message` — human-readable per contract
 * §2 — never a raw thrown value. `onRetry` is omitted (not just hidden) when
 * absent, rather than rendered disabled, since a 403/404 genuinely isn't
 * fixed by retrying.
 */
export function ErrorState({
  message,
  onRetry,
  className,
}: {
  message: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div className={cn('rounded-xl border border-danger-soft bg-danger-soft p-4 text-[13px] text-danger-ink', className)}>
      <div>{message}</div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 font-semibold underline underline-offset-2"
        >
          Try again
        </button>
      )}
    </div>
  );
}
